import { Fragment, useState, useCallback, useRef } from "react";
import { FaChevronLeft, FaChevronRight, FaEnvelope, FaPhoneAlt } from "react-icons/fa";
import useEscapeKey from "../utils/useEscapeKey";

const CYCLE_MS = 6000;
const SWIPE_PX = 50; // Minsta horisontella svep för att byta person

const EmployeeSelector = ({ employees }) => {
  const [index, setIndex] = useState(0);
  const [locked, setLocked] = useState(false);
  const [paused, setPaused] = useState(false);
  const [dragX, setDragX] = useState(0);
  const touch = useRef(null);

  const next = () => setIndex((prev) => (prev + 1) % employees.length);

  // Manuell navigering (svep/pilar) låser precis som ett tryck på en thumbnail
  const step = (delta) => {
    setIndex((prev) => (prev + delta + employees.length) % employees.length);
    setLocked(true);
  };

  const onTouchStart = (e) => {
    const { clientX: x, clientY: y } = e.touches[0];
    touch.current = { x, y, dx: 0, horizontal: null };
  };

  const onTouchMove = (e) => {
    const start = touch.current;
    if (!start) return;
    const dx = e.touches[0].clientX - start.x;
    const dy = e.touches[0].clientY - start.y;
    // Avgör riktning först när fingret rört sig lite, så vertikal scroll inte kapas
    if (start.horizontal === null && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) {
      start.horizontal = Math.abs(dx) > Math.abs(dy);
    }
    if (start.horizontal) {
      start.dx = dx;
      setDragX(dx * 0.4); // Motstånd så kortet följer fingret utan att flyga iväg
    }
  };

  const onTouchEnd = () => {
    const start = touch.current;
    touch.current = null;
    setDragX(0);
    if (start?.horizontal && Math.abs(start.dx) >= SWIPE_PX) {
      step(start.dx < 0 ? 1 : -1);
    }
  };

  const release = useCallback(() => setLocked(false), []);
  useEscapeKey(release, locked);

  const select = (i) => {
    if (locked && i === index) {
      setLocked(false);
    } else {
      setIndex(i);
      setLocked(true);
    }
  };

  return (
    <div
      className="grid lg:grid-cols-[1fr_1.15fr] gap-4 sm:gap-8 items-start"
      onPointerEnter={(e) => e.pointerType === "mouse" && setPaused(true)}
      onPointerLeave={(e) => e.pointerType === "mouse" && setPaused(false)}
    >
      {/* Roster */}
      <div className="order-2 lg:order-1 grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-4 gap-2 sm:gap-3">
        {employees.map((employee, i) => {
          const active = i === index;
          const startsGroup = employee.group && employee.group !== employees[i - 1]?.group;
          return (
            <Fragment key={employee.name}>
              {startsGroup && (
                <div className="col-span-full mt-2 flex items-center gap-3">
                  <span className="eyebrow">{employee.group}</span>
                  <span className="h-px flex-1 bg-slate-200 dark:bg-white/10" />
                </div>
              )}
              <button
                onClick={() => select(i)}
                aria-pressed={locked && active}
                aria-label={employee.name}
                className={`group relative aspect-square overflow-hidden rounded-xl border-2 transition-colors duration-300 ${
                  active ? "border-sky-500" : "border-transparent hover:border-sky-500/50"
                }`}
              >
                <img
                  src={employee.avatar}
                  alt=""
                  width={800}
                  height={800}
                  className={`w-full h-full object-cover transition-opacity duration-300 ${
                    active ? "opacity-100" : "opacity-50 group-hover:opacity-100"
                  }`}
                />
                <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent px-1 pt-4 pb-1 text-[10px] sm:text-xs text-white truncate">
                  {employee.name.split(" ")[0]}
                </span>
              </button>
            </Fragment>
          );
        })}
      </div>

      {/* Hero: every panel shares one grid cell, so the card is always as tall as the longest bio */}
      <div
        className="order-1 lg:order-2 lg:sticky lg:top-24 card relative overflow-hidden touch-pan-y"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
      >
        <div
          className={`grid ${dragX === 0 ? "transition-transform duration-200 motion-reduce:transition-none" : ""}`}
          style={dragX ? { transform: `translateX(${dragX}px)` } : undefined}
        >
          {employees.map((employee, i) => {
            const active = i === index;
            return (
              <div
                key={employee.name}
                aria-hidden={!active}
                className={`[grid-area:1/1] flex flex-col gap-5 p-5 sm:gap-6 sm:p-8 transition-opacity duration-500 motion-reduce:transition-none ${
                  active ? "opacity-100" : "opacity-0 pointer-events-none"
                }`}
              >
                <div className="flex items-center gap-4 sm:gap-5">
                  <img
                    src={employee.avatar}
                    alt={employee.name}
                    width={800}
                    height={800}
                    className="w-24 h-24 sm:w-56 sm:h-56 xl:w-64 xl:h-64 shrink-0 rounded-xl object-cover"
                  />
                  <div className="flex flex-col gap-2 min-w-0">
                    <p className="font-mono text-xs uppercase tracking-widest text-sky-500">
                      {employee.title}
                    </p>
                    {/* Förnamn och efternamn på var sin rad, oavsett hur brett utrymmet är. Mellanslaget före <br> behålls så namnet läses upp som en enhet. */}
                    <h2 className="text-xl sm:text-3xl font-semibold tracking-tight text-slate-900 dark:text-white">
                      {employee.name.split(" ")[0]}{" "}
                      <br />
                      {employee.name.split(" ").slice(1).join(" ")}
                    </h2>
                    {employee.certifications.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {employee.certifications.map((cert) => (
                          // tabIndex gör att tooltipen även visas vid tangentbordsfokus och tap på mobil
                          <span key={cert.name} tabIndex={active ? 0 : -1} className="group/cert relative rounded-full outline-none">
                            <img
                              src={cert.logo}
                              alt={cert.name}
                              className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-white object-cover shadow transition-transform duration-200 group-hover/cert:scale-110 group-focus/cert:scale-110"
                            />
                            <span
                              role="tooltip"
                              className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-2 -translate-x-1/2 translate-y-1 whitespace-nowrap rounded-md bg-slate-900 px-2 py-1 font-mono text-[11px] text-white opacity-0 shadow-lg transition duration-150 group-hover/cert:translate-y-0 group-hover/cert:opacity-100 group-focus/cert:translate-y-0 group-focus/cert:opacity-100 dark:bg-white dark:text-slate-900"
                            >
                              {cert.name}
                            </span>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {employee.text && (
                  <p className="text-sm leading-relaxed text-slate-600 sm:text-base dark:text-slate-400">
                    {employee.text}
                  </p>
                )}

                {employee.contact && (
                  <div className="mt-auto flex flex-col gap-2 border-t border-slate-200 pt-5 sm:flex-row sm:flex-wrap dark:border-white/10">
                    {[
                      { href: `tel:${employee.contact.phoneHref}`, icon: FaPhoneAlt, label: employee.contact.phone },
                      { href: `mailto:${employee.contact.email}`, icon: FaEnvelope, label: employee.contact.email },
                    ].map(({ href, icon: Icon, label }) => (
                      <a
                        key={href}
                        href={href}
                        tabIndex={active ? 0 : -1}
                        className="group/contact inline-flex min-w-0 items-center gap-3 rounded-xl border border-slate-200 px-4 py-2.5 text-sm text-slate-700 transition-colors duration-200 hover:border-sky-500 hover:text-sky-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 dark:border-white/10 dark:text-slate-300"
                      >
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-sky-500/10 text-sky-500 transition-colors duration-200 group-hover/contact:bg-sky-500 group-hover/contact:text-slate-950">
                          <Icon size={13} />
                        </span>
                        <span className="min-w-0 break-words">{label}</span>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Pilar för mobil, svep fungerar men syns inte */}
        <div className="flex items-center justify-between px-5 pb-4 lg:hidden">
          <button
            type="button"
            onClick={() => step(-1)}
            aria-label="Föregående person"
            className="flex h-9 w-9 items-center justify-center rounded-full text-slate-500 transition-colors hover:text-sky-500 dark:text-slate-400"
          >
            <FaChevronLeft size={14} />
          </button>
          <span className="font-mono text-xs tracking-widest text-slate-500 dark:text-slate-400">
            {String(index + 1).padStart(2, "0")} / {String(employees.length).padStart(2, "0")}
          </span>
          <button
            type="button"
            onClick={() => step(1)}
            aria-label="Nästa person"
            className="flex h-9 w-9 items-center justify-center rounded-full text-slate-500 transition-colors hover:text-sky-500 dark:text-slate-400"
          >
            <FaChevronRight size={14} />
          </button>
        </div>

        {/* Autoplay progress: the bar's animation is the timer, so pausing it pauses the cycle */}
        {!locked && (
          <div
            key={index}
            onAnimationEnd={next}
            className="absolute inset-x-0 bottom-0 h-1 origin-left bg-sky-500 animate-progress"
            style={{
              animationDuration: `${CYCLE_MS}ms`,
              animationPlayState: paused ? "paused" : "running",
            }}
          />
        )}
      </div>
    </div>
  );
};

export default EmployeeSelector;
